/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
    bool, func, object, shape, string,
} from 'prop-types';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Paper from '@material-ui/core/Paper';
import InputBase from '@material-ui/core/InputBase';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { gql, useLazyQuery } from '@apollo/client';
import mbpLogger from '@module-logger/mbp';

import SearchIcon from '../../BrandTheme/Icons/SearchIcon';
import { getDeviceType, getFeatureFlag } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import SearchClearIcon from '../../BrandTheme/Icons/SearchClearIcon';
import useExperimentServiceAttributes from '../../../helpers/experimentService/useExperimentServiceAttributes';
import SearchSuggestions from './SearchSuggestions/SearchSuggestions';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { emitCustomTrackingEvent } from '../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import useRecentlySearched from '../../../helpers/useRecentlySearched';
import SearchTypeAheadSuggestions from './SearchSuggestions/SearchTypeAheadSuggestions';
import { GRAPHQL_ENV } from '../../../gql';
import { getCurrentPageType } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';

const useStyles = makeStyles((theme) => ({
    root: {
        flex: 1,
        maxWidth: 727,
        width: '100%',
        position: 'relative',
        margin: 'auto',
    },
    searchbarMain: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    searchbarDivider: {
        padding: '10px 4px 10px 12px',
        borderBottom: '1px solid #E9E9E9',
    },
    paperRoot: {
        padding: '10px 8px 10px 12px',
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: '40px',
        border: '1px solid #C4C4C4',
        borderRadius: '3px',
        position: 'relative',
        boxSizing: 'border-box',
        '&:focus-within': {
            border: '1px solid #000000',
        },
    },
    customPlaceHolder: {
        position: 'absolute',
        top: '50%',
        left: '12px',
        transform: 'translateY(-50%)',
        color: '#555555',
        pointerEvents: 'none',
        fontSize: '14px',
        '& sup': {
            fontSize: '10px',
        },
    },
    inputRoot: {
        fontSize: '14px !important',
        flex: 1,
        color: '#000000',
        // fontSize is 14px, so mobile browsers zoom the page when the input is focused
        // To handle that making fontSize as 16px and reducing the size through transform and make a fontSize look like 14px
        '@media (max-width: 1025px)': {
            fontSize: '16px !important',
            transform: 'scale(0.875)',
            transformOrigin: 'left center',
            width: '114.28%', // Compensate for scale: 100% / 0.875
        },
    },
    input: {
        padding: 0,
        height: '100%',
    },
    clearSearchButton: {
        padding: '5px',
        [theme.breakpoints.up(1026)]: {
            padding: '0px 5px',
        },
    },
    searchIcon: {
        display: 'inline-flex',
        position: 'relative',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1px',
        pointer: 'none',
    },
    cancelButton: {
        fontWeight: 700,
        fontSize: '14px',
        lineHeight: '20px',
        textTransform: 'none',
        borderBottom: '2px solid #000000',
    },
    searchButton: {
        color: `${theme.palette.white} !important`,
        paddingLeft: '20px !important',
        paddingRight: '20px !important',
        textTransform: 'none !important',
        fontSize: '14px !important',
        fontWeight: 'bold !important',
        height: '40px !important',
        backgroundColor: `${theme.palette.primaryButton} !important`,
        '-webkit-font-smoothing': 'antialiased !important',
        '&:hover': {
            backgroundColor: `${theme.palette.primaryButton} !important`,
        },
        '&:focus': {
            outline: `1px solid  ${theme.palette.cms?.primary || theme.palette.colorPrimary} !important`,
            outlineColor: `${theme.palette.cms?.primary || theme.palette.colorPrimary} !important`,
        },
    },
}));

const cleanTerm = (input) => {
    // removes extra spaces an ., example input +_)(*()()_    roses . +_)()_)))() red +_)()_))

    const a = input.replace(/[`~+!@#$%^&*()_|=?;:'",.<>{}[\]\\]/g, ''); // replaces all special chars with spaces
    const b = a.replace(/\s+/g, ' '); // removes extra consecutive spaces with just one space
    const c = b.replace(/^\s+|\s+$/g, ''); // removes spaces in the beginning and end of search term
    // output would be "roses+red" for the url
    if (!/[a-zA-Z0-9]/.test(c)) return ''; // Disallow if there's no alphanumeric character
    return c;
};

const concatSearchTerm = (url, value) => {
    let query = '';
    if (url.indexOf('searchTerm=') >= 0) {
        query += '';
    } else {
        // replaces spaces between words with _
        query += `${url.indexOf('?') >= 0 || query.indexOf('?') >= 0 ? '&' : '?'}searchTerm=${cleanTerm(value)?.trim().replace(/ /g, '_').toLowerCase()}`;
    }
    return `${url}${query}`;
};

const FIND_TRENDING_SEARCHES = gql`
    query FindTrendingSearches($brand: String!, $environment: String!) {
        findTrendingSearches(brand: $brand, environment: $environment) {
            trendingSearches {
                phrase
                rank
            }
        }
    }`;

function SearchV3({
    handleMobileClose, autoFocus, brand, isDesktop, salesforceResponse,
}) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();

    const deviceType = useSelector(getDeviceType);
    const { addRecentlySearched } = useRecentlySearched();

    const { targeting, isGraphqlTargetingEnabled, experimentService } = useExperimentServiceAttributes({ queryName: 'autoSuggest' });

    const typeAheadPopularSearches = useSelector(getFeatureFlag('is-search-ui-type-ahead-individual'))?.popularSearches;

    const [searchValue, setSearchValue] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [showTypeAheadSuggestions, setShowTypeAheadSuggestions] = useState(!isDesktop);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
    const [recentlySearchedIndex, setRecentlySearchedIndex] = useState(-1);
    const inputRef = useRef(null);
    const inputBaseRef = useRef(null);
    const searchContainerRef = useRef(null);
    const suggestionsDataRef = useRef(null);
    const recentlySearchedDataRef = useRef(null);
    const placeHolder = 'Search';

    const pageType = useSelector(getCurrentPageType);

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
            // Move cursor to end of text if there's existing text
            const length = inputRef.current.value.length;
            inputRef.current.setSelectionRange(length, length);
        }
    }, [autoFocus]);

    const [getTrendingSearches, { error }] = useLazyQuery(FIND_TRENDING_SEARCHES);

    useEffect(() => {
        if (typeAheadPopularSearches && brand?.domain) {
            // Preload trending searches query to avoid delay when opening type ahead suggestions
            getTrendingSearches({
                variables: {
                    brand: brand.domain,
                    environment: GRAPHQL_ENV,
                },
                apolloOptions: {
                    fetchPolicy: 'cache-first',
                },
            });
        }
    }, [typeAheadPopularSearches, brand?.domain]);

    if (error) {
        mbpLogger.logError({
            function: 'SearchV3.js',
            message: 'Error loading data for trending searches',
            appName: process.env.npm_package_name,
            module: 'SearchV3',
            error,
        });
    }

    // reset impression flag on page change to track impression once in a page
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.wuAutoSuggestImpressionFired = false;
        }
        if (inputRef.current) {
            inputRef.current.blur();
        }
    }, [history.location.pathname]);

    useEffect(() => {
        if (!isDesktop) return undefined;

        const handleClickOutside = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                setShowSuggestions(false);
                setShowTypeAheadSuggestions(false);
            }
        };

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                setShowSuggestions(false);
                setShowTypeAheadSuggestions(false);
                if (inputRef.current) {
                    inputRef.current.blur();
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscapeKey);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isDesktop]);

    const trackExperiment = (eventName = 'search', label = null) => {
        if (salesforceResponse?.campaign?.campaignResponses?.[0]?.campaignName) {
            dispatch(trackEvent({
                eventCategory: 'Experiment',
                eventAction: salesforceResponse?.campaign?.campaignResponses?.[0]?.campaignName,
                eventLabel: salesforceResponse?.campaign?.campaignResponses?.[0]?.payload?.userGroup === 'Test' ? 'Variant' : 'Control',
                eventName,
            }));
            if (label) {
                dispatch(emitCustomTrackingEvent({
                    action: 'Click',
                    salesforceResponse,
                    page: { type: pageType },
                    eventSubType: 'campaign-tracking',
                    label,
                    category: 'Search',
                }));
            }
        }
    };

    const handleSubmit = (e, selectedSuggestion = null) => {
        e?.preventDefault();

        // If a suggestion is selected via keyboard, use that
        if (selectedSuggestion) {
            setSearchValue(selectedSuggestion);
            dispatch(trackEvent({
                eventCategory: 'Search',
                eventAction: 'search_suggest_click',
                eventLabel: 'SEARCH INTERACTION | <<pageType>>',
                searchVariety: 'search_suggest_click',
                searchPhrase: selectedSuggestion.toLowerCase(),
                eventName: 'search interaction',

                search_type: 'search_suggest_click',
                search_term: selectedSuggestion,
                unique_search_term: searchValue,

            }));
            dispatch(emitCustomTrackingEvent({
                eventType: 'custom',
                eventSubType: 'feature-tracking',
                action: 'Click',
                category: 'Search',
                label: 'autocomplete',
            }));
            trackExperiment('suggestion', 'autocomplete');
        }
        const cleanSearchInput = selectedSuggestion ? cleanTerm(selectedSuggestion) : cleanTerm(searchValue);
        let searchTermValue = cleanSearchInput
            .trim()
            .replace(/ /g, '+') // replaces spaces between words with +
            .toLowerCase();

        if (searchTermValue?.length !== 0 && searchTermValue !== 'notfound') {
            if (!selectedSuggestion) {
                dispatch(trackEvent({
                    eventCategory: 'Search',
                    eventAction: searchValue,
                    eventLabel: 'MANUAL | <<pageType>>',
                    searchVariety: 'Manual',
                    searchPhrase: searchValue,
                    eventName: 'search',

                    search_type: 'manual',
                    search_term: searchValue?.toLowerCase?.(),
                    unique_search_term: searchValue,
                }));
                dispatch(emitCustomTrackingEvent({
                    eventType: 'custom',
                    eventSubType: 'feature-tracking',
                    action: 'Click',
                    category: 'Search',
                    label: 'Submit Search',
                    tag: searchValue?.toLowerCase?.(),
                }));
                trackExperiment('search', 'search_button');
            }
            // Handle suggestion state before moving to search page
            setShowSuggestions(false);
            setShowTypeAheadSuggestions(false);
            handleMobileClose();
            if (brand?.['search-append-searchterm-url-param']) {
                searchTermValue = concatSearchTerm(searchTermValue, cleanSearchInput);
            }
            addRecentlySearched({ searchTerm: selectedSuggestion || searchValue, value: cleanSearchInput });
            history.push({
                pathname: `/searchterm/${searchTermValue}`,
                state: { searchInputTerm: cleanSearchInput || '' },
            });
        }
    };

    const hasSuggestions = cleanTerm(searchValue)?.length >= 1;

    const handleSearchValueChange = (e) => {
        setSearchValue(e.target.value);
        setSelectedSuggestionIndex(-1); // Reset selection when typing
        setShowSuggestions(true);
        if (e.target.value.length === 0) {
            setShowTypeAheadSuggestions(true);
        } else {
            setShowTypeAheadSuggestions(false);
        }
    };

    const handleKeyDown = (e) => {
        if (hasSuggestions && showSuggestions) {
            const suggestionsCount = suggestionsDataRef.current?.length || 0;
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
            }

            if (e.key === 'ArrowDown') {
                setSelectedSuggestionIndex((prev) => {
                    if (prev < suggestionsCount - 1) {
                        return prev + 1;
                    }
                    return prev;
                });
            } else if (e.key === 'ArrowUp') {
                setSelectedSuggestionIndex((prev) => {
                    if (prev > -1) {
                        return prev - 1;
                    }
                    return -1;
                });
            } else if (e.key === 'Enter') {
                if (selectedSuggestionIndex >= 0 && suggestionsDataRef.current?.[selectedSuggestionIndex]?.inputString) {
                    e.preventDefault();
                    setShowSuggestions(false);
                    setShowTypeAheadSuggestions(false);
                    handleMobileClose();
                    handleSubmit(e, suggestionsDataRef.current[selectedSuggestionIndex]?.inputString);
                }
                // If selectedSuggestionIndex is -1, let the form submit naturally
            }
        }
        if (showTypeAheadSuggestions) {
            const recentlySearchedCount = recentlySearchedDataRef.current?.length || 0;
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
            }

            if (e.key === 'ArrowDown') {
                setRecentlySearchedIndex((prev) => {
                    if (prev < recentlySearchedCount - 1) {
                        return prev + 1;
                    }
                    return prev;
                });
            } else if (e.key === 'ArrowUp') {
                setRecentlySearchedIndex((prev) => {
                    if (prev > -1) {
                        return prev - 1;
                    }
                    return -1;
                });
            } else if (e.key === 'Enter') {
                if (recentlySearchedIndex >= 0 && recentlySearchedDataRef.current?.[recentlySearchedIndex]?.inputString) {
                    e.preventDefault();
                    setShowSuggestions(false);
                    setShowTypeAheadSuggestions(false);
                    handleMobileClose();
                    handleSubmit(e, recentlySearchedDataRef.current[recentlySearchedIndex]?.inputString);
                }
            }
        }
        return;
    };

    const handleFocus = () => {
        setShowTypeAheadSuggestions(true);
    };

    const handleSearchClick = () => {
        setSelectedSuggestionIndex(-1);
        dispatch(trackEvent({
            eventCategory: 'Search',
            eventAction: 'search bar click',
            eventLabel: 'SEARCH INTERACTION | <<pageType>>',
            eventName: 'search interaction',
        }));
        dispatch(emitCustomTrackingEvent({
            eventType: 'custom',
            eventSubType: 'feature-tracking',
            action: 'Click',
            category: 'Search',
            label: 'search_input',
        }));
        trackExperiment('search interaction');
    };

    return (
        <div role="search" aria-label="search" className={classes.root} ref={searchContainerRef}>
            <form onSubmit={handleSubmit} className={`${isDesktop ? '' : ` ${classes.searchbarDivider}`}`} role="search">
                <div className={classes.searchbarMain}>
                    <Paper elevation={0} component="div" className={classes.paperRoot} ref={inputBaseRef}>
                        {!searchValue && (
                            <span className={classes.customPlaceHolder} aria-hidden="true">
                                {placeHolder}
                            </span>
                        )}
                        <InputBase
                            id={`SearchBox_${deviceType}`}
                            name={`searchTerm_${deviceType}`}
                            inputRef={inputRef}
                            onClick={handleSearchClick}
                            onFocus={handleFocus}
                            value={searchValue}
                            onChange={handleSearchValueChange}
                            onKeyDown={handleKeyDown}
                            autoComplete="off"
                            type="text"
                            classes={{
                                root: classes.inputRoot,
                                input: classes.input,
                            }}
                            inputProps={{
                                'aria-label': 'Search',
                                'aria-autocomplete': 'list',
                                'aria-controls': hasSuggestions ? 'suggestion' : undefined,
                                'aria-expanded': hasSuggestions,
                                'aria-owns': hasSuggestions ? 'suggestion' : undefined,
                                'aria-activedescendant': selectedSuggestionIndex >= 0 ? `suggestion-${selectedSuggestionIndex}` : undefined,
                                'data-test': 'header-search-box-v3', // do NOT change or remove data-test attribute
                                'data-testid': 'header-search-box-v3',
                                enterKeyHint: 'search',
                                maxLength: 250,
                                role: 'combobox',
                            }}
                        />
                        {searchValue?.length > 0 && (
                            <IconButton
                                onClick={() => {
                                    setSearchValue('');
                                    if (inputRef.current) {
                                        inputRef.current.focus();
                                    }
                                }}
                                aria-label="Clear search input"
                                className={classes.clearSearchButton}
                                size="small"
                            >
                                <SearchClearIcon fillColor="#2F363D" />
                            </IconButton>
                        )}
                        {!searchValue && !isDesktop && <div className={classes.searchIcon} aria-hidden="true"><SearchIcon fillColor="#2F363D" /></div>}
                    </Paper>
                    {isDesktop
                        ? (
                            <Button
                                className={classes.searchButton}
                                id="btn-search"
                                data-test="hp-header-search-button"
                                name="hpSearchButton"
                                type="submit"
                                aria-label="Submit search"
                            >
                                Search
                            </Button>
                        )
                        : (
                            <Button
                                variant="text"
                                classes={{ label: classes.cancelButton }}
                                onClick={() => {
                                    handleMobileClose();
                                    setShowSuggestions(false);
                                }}
                                aria-label="Cancel search and close search bar"
                            >
                                Cancel
                            </Button>
                        )}
                </div>
            </form>
            {hasSuggestions && showSuggestions && (
                <SearchSuggestions
                    phrase={cleanTerm(searchValue)}
                    setSearchValue={setSearchValue}
                    brand={brand.domain}
                    targetingEnabled={isGraphqlTargetingEnabled}
                    experimentService={experimentService}
                    targeting={targeting}
                    cleanTerm={cleanTerm}
                    concatSearchTerm={concatSearchTerm}
                    isDesktop={isDesktop}
                    inputWidth={inputBaseRef?.current?.offsetWidth}
                    setShowSuggestions={setShowSuggestions}
                    selectedSuggestionIndex={selectedSuggestionIndex}
                    suggestionsDataRef={suggestionsDataRef}
                    handleMobileClose={handleMobileClose}
                    trackExperiment={trackExperiment}
                />
            )}
            {showTypeAheadSuggestions && (
                <SearchTypeAheadSuggestions
                    isDesktop={isDesktop}
                    phrase={cleanTerm(searchValue)}
                    setSearchValue={setSearchValue}
                    cleanTerm={cleanTerm}
                    concatSearchTerm={concatSearchTerm}
                    inputWidth={inputBaseRef?.current?.offsetWidth}
                    setShowSuggestions={setShowSuggestions}
                    setShowTypeAheadSuggestions={setShowTypeAheadSuggestions}
                    recentlySearchedIndex={recentlySearchedIndex}
                    recentlySearchedDataRef={recentlySearchedDataRef}
                    handleMobileClose={handleMobileClose}
                    trackExperiment={trackExperiment}
                />
            )}
        </div>
    );
}

SearchV3.propTypes = {
    handleMobileClose: func,
    autoFocus: bool,
    brand: shape({
        domain: string.isRequired,
    }).isRequired,
    isDesktop: bool,
    salesforceResponse: object,
};

SearchV3.defaultProps = {
    handleMobileClose: () => { },
    salesforceResponse: { },
    autoFocus: false,
    isDesktop: false,
};

export default SearchV3;
